import React from 'react'
import { useGetTreeById } from '../reducers/trees_hooks'
import { nodeAttributionLeaves } from '../components/utility/TreeHelpers'
import { AttributionValidation } from './AttributionValidation'
import { allProductsByPrimaryTreeId } from '../api/masters.atoms'
import { MigrateSchools } from '../components/tree/masters/MigrateSchools'
import { Versions } from '../components/tree/masters/Versions'
import { MasterUpdateValidation } from '../components/tree/masters/MasterUpdateValidation'
import { EditableCopyView } from './EditableCopyView'
import { SummaryValidation } from './summaries/SummaryValidation'
import { TrackedCopiesTree } from '../components/tree/trackedCopies/TrackedCopies'
import { doAdminQueries, IQueryInfo } from '../providers/mutations'
import { ManageMetadata } from '../components/resources/products/ManageMetadata'
import { SupportedPlatformFeatures } from '../components/resources/products/SupportedPlatformFeatures'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { useAllPartnerNames } from 'shared/hooks/partners.hooks'

export const mastersMenuOptions = [
  {
    title: 'Attribution Validation',
    component: AttributionValidation,
  },
  {
    title: 'Summary Validation',
    component: SummaryValidation,
  },
]

export function useCheckTreeAttributions(ids: {
  treeId: string
  versionId: string
}) {
  const attributions = useAllPartnerNames()
  const { tree } = useGetTreeById(ids)
  if (!tree) {
    return
  }
  const attributionLeaves = nodeAttributionLeaves(tree)
  const treeAttributions = attributionLeaves.map(({ body: { value } }) => value)
  return {
    attributions: treeAttributions,
    tree,
    valid:
      treeAttributions.length === 1 &&
      attributions?.includes(treeAttributions[0]),
  }
}

interface ProductWithTreeData {
  primary_tree: {
    tree_id: string
    version_id: string
  }
  editable_tree: {
    tree_id: string
    version_id: string
  }
}

export interface GroupWithProducts {
  id: number
  name: string
  description?: string
  products: ProductWithTreeData[]
}

export async function getGroupForId(groupId: number) {
  const queryInfo: IQueryInfo = {
    resourceData: {
      key: 'getGroupByID',
      fields: [
        'id',
        'name',
        {
          products: [
            'id',
            'title',
            { primary_tree: ['tree_id', 'version_id'] },
            { editable_tree: ['tree_id', 'version_id'] },
          ],
        },
      ],
    },
    params: { id: groupId },
  }
  const { data } = await doAdminQueries<GroupWithProducts>([queryInfo])
  return data.getGroupByID
}

export function useMasterTitle(masterId: string) {
  const product = useAtomValue(allProductsByPrimaryTreeId)[masterId]
  return product?.title ?? ''
}

export enum MasterManagementTools {
  migrate = 'Migrate',
  versions = 'Versions',
  updateValidation = 'Master Update Validation',
  editableCopy = 'Editable Copy',
  trackedCopies = 'Tracked Copies',
  manageMetadata = 'Manage Metadata',
  supportedPlatformFeatures = 'Supported Platform Features',
}

export function useMasterManagementTools(masterId: string) {
  const product = useAtomValue(allProductsByPrimaryTreeId)[masterId]
  if (!product) return []

  return [
    {
      title: MasterManagementTools.migrate,
      component: () => <MigrateSchools productId={product.id} />,
    },
    {
      title: MasterManagementTools.versions,
      component: () => <Versions productId={product.id} />,
    },
    {
      title: MasterManagementTools.updateValidation,
      component: () => <MasterUpdateValidation productId={product.id} />,
    },
    {
      title: MasterManagementTools.editableCopy,
      component: () => <EditableCopyView productId={product.id} />,
    },
    {
      title: MasterManagementTools.trackedCopies,
      component: () => <TrackedCopiesTree productId={product.id} />,
    },
    {
      title: MasterManagementTools.manageMetadata,
      component: () => <ManageMetadata productId={product.id} />,
    },
    {
      title: MasterManagementTools.supportedPlatformFeatures,
      component: () => <SupportedPlatformFeatures productId={product.id} />,
    },
  ]
}
